import { TandemTool } from "../../base/TandemTool.js";
import { logger } from "../../../../../../core/logger.js";

export class ReadFileTool extends TandemTool {
  constructor() {
    super();
    this.toolName = "readFileTool";
  }

  /**
   * @param {string} path
   * @param {string} [encoding='utf8'] - utf8 | binary | base64
   * @returns {Promise<Object>}
   */
  async readFile(path, encoding = "utf8") {
    try {
      if (!path || typeof path !== "string") {
        return this.returnError({}, "Valid file path required");
      }

      const api = this.getTandemLocalAppApi();
      if (!api?.readFile) {
        return this.returnError(
          {},
          "Tandem file reads require Revolt Desktop (tandemAPI.readFile)."
        );
      }

      const root = this.getTandemWorkingDirectoryRoot();
      if (!root) {
        return this.returnError(
          {},
          "Tandem has no project folder set (working directory path)."
        );
      }

      const normalizedPath = this.normalizeAndValidatePath(path);
      if (!normalizedPath) {
        return this.returnError({}, "Invalid file path");
      }
      const relativePath = normalizedPath.replace(/^\/+/, "");

      const enc =
        encoding === "binary" || encoding === "base64" ? encoding : "utf8";

      const res = await api.readFile({
        rootPath: root,
        relativePath,
        encoding: enc,
      });

      if (!res?.success) {
        return this.returnError({}, res?.error || "Failed to read file on disk");
      }

      logger.log(
        { tags: "tools|tandem|readfile|info", color1: "lime" },
        `Tandem read file: ${normalizedPath}`,
        { size: res.size, encoding: enc }
      );

      return this.returnSuccess(
        {
          path: normalizedPath,
          content: res.content,
          encoding: enc,
          size: res.size,
        },
        "File read successfully"
      );
    } catch (error) {
      logger.error(
        { tags: "tools|tandem|readfile|error", color1: "red" },
        `Tandem failed to read file: ${path}`,
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Whether a path exists under the tandem project root (file or directory).
   * @param {string} path
   * @returns {Promise<boolean>}
   */
  async checkExists(path) {
    try {
      if (!path || typeof path !== "string") {
        return false;
      }
      const api = this.getTandemLocalAppApi();
      if (!api?.getPathInfo) {
        return false;
      }
      const root = this.getTandemWorkingDirectoryRoot();
      if (!root) {
        return false;
      }
      const normalizedPath = this.normalizeAndValidatePath(path);
      if (!normalizedPath) {
        return false;
      }
      const relativePath = normalizedPath.replace(/^\/+/, "");
      const info = await api.getPathInfo({
        rootPath: root,
        relativePath,
      });
      return !!(info?.success && info.exists);
    } catch {
      return false;
    }
  }
}
